import React, { useCallback, useEffect, useRef, useState } from "react";
import type { StreamingEvent } from "../../api/api";
import { StreamingEventTypeAssertions } from "../../api/api";
import { makeStyles } from "@mui/styles";
import { reloadAllState, store, useAppDispatch } from "../../state/Store";
import {
    ConfirmStreamingEventsServiceClient,
    OpenStreamingConnectionServiceClient
} from "../../api/grpc/Streaming.client";
import { GrpcWebFetchTransport } from "@protobuf-ts/grpcweb-transport";
import { useTabVisibility } from "../../util/hooks/useTabVisibility";
import { updateExecutionsManually } from "../../state/ExecutionsState";
import { updateRolesManually } from "../../state/RolesState";
import { updateScenariosManually } from "../../state/ScenariosState";
import { updateWorkersManually } from "../../state/WorkersState";
import { assertNever } from "../../util/TypeUtils";
import {
    ConfirmRequest,
    ConfirmRequest_Event,
    ConnectRequest,
    ConnectRequest_ConnectReason
} from "../../api/grpc/Streaming";
import { getMetadata } from "../../util/AuthorizationUtils";
import { Base64 } from "js-base64";
import Dialog from "@mui/material/Dialog";
import { CircularProgress, DialogTitle } from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import LoginService from "../../util/LoginService";
import { updateExecutionErrorsManually } from "../../state/ExecutionErrorsState";
import { v4 } from "uuid";

interface InitEvent {
    eventName: "INIT",
    data: {
        connectionId: string;
    }
}

interface IncomingPingEvent {
    eventType: "PING";
}

interface IncomingStreamingEvent {
    eventType: "STREAMING";
    data: StreamingEvent;
}

interface IncomingInitEvent {
    eventType: "INIT";
    data: InitEvent;
}

type IncomingEventData = IncomingPingEvent | IncomingStreamingEvent | IncomingInitEvent;

interface IncomingEvent {
    event: {
        application: string;
    } & IncomingEventData;
    id: string;
}

const useStyles = makeStyles(() => ({
    paper: {
        margin: "-0.5rem",
        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        maxWidth: "600px",
        width: "calc(100vw - 0.2rem)"
    },
    loadingWrapper: {
        padding: "1rem"
    },
    dialog: {
        textAlign: "center"
    }
}));

let reconnectDialogTimeout: NodeJS.Timeout | undefined;
let pingTimeout: NodeJS.Timeout | undefined;
let reconnectErrorTimeout: NodeJS.Timeout | undefined;
let abortController: AbortController | undefined;
let firstTime = true;

const EventStreamer: React.FC = () => {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    const lastConnectionTime = useRef(0);
    const connected = useRef(false);
    const connectionId = useRef("");

    const processing = useRef<boolean>(false);
    const unprocessedEvents = useRef<StreamingEvent[]>([]);
    const unacknowledgedEvents = useRef<string[]>([]);

    const connectClient = useRef(new OpenStreamingConnectionServiceClient(new GrpcWebFetchTransport({
        baseUrl: "/"
    })));
    const confirmClient = useRef(new ConfirmStreamingEventsServiceClient(new GrpcWebFetchTransport({
        baseUrl: "/"
    })));

    const [key, setKey] = useState(0);
    const [connectedState, setConnectedState] = useState(false);
    const [connectedOnce, setConnectedOnce] = useState(false);

    const changedTabVisibility = useTabVisibility();
    const [triedToReconnect, setTriedToReconnect] = useState(false);

    const reconnect = useCallback(() => {
        // If tab is hidden, do not try to reconnect, because browser energy save mode won't confirm events
        // changedTabVisibility can not be used, because it would rerender the component
        if (document.visibilityState === "hidden") {
            console.log("Tab is hidden, not reconnecting.");
            setTriedToReconnect(true);
            return;
        }
        // If the last connection attempt was made less than 1 second ago,
        // schedule it instead
        if (lastConnectionTime.current < new Date().getTime() - 1000) {
            setKey(current => current + 1);
        } else {
            if (reconnectErrorTimeout) {
                clearTimeout(reconnectErrorTimeout);
            }
            reconnectErrorTimeout = setTimeout(() => setKey(current => current + 1), 1000);
        }
    }, []);

    useEffect(() => {
        // reconnect if tab is visible again
        if (changedTabVisibility === "visible" && triedToReconnect) {
            reconnect();
            setTriedToReconnect(false);
            console.log("Tab is visible again, reconnecting...");
        }
    }, [changedTabVisibility, reconnect, triedToReconnect]);

    const onPing = useCallback(() => {
        // Reset a pending reconnect dialog timeout
        reconnectDialogTimeout && clearTimeout(reconnectDialogTimeout);

        // If the connection status is false
        if (!connected.current) {
            // Set the connection status to true
            connected.current = true;
            setConnectedState(true);

            // If it is not the first time
            if (!firstTime) {
                // Reload all data in state because we might have missed some events while we
                // were disconnected
                reloadAllState(dispatch);
            } else {
                setConnectedOnce(true);
                firstTime = false;
            }
        }

        // Clear the ping timeout and set a new one on each ping
        pingTimeout && clearTimeout(pingTimeout);
        pingTimeout = setTimeout(() => {
            // As soon as there was no ping for at least 5 seconds, set the connection state to
            // false and try to reconnect. After another 5 seconds, show the reconnecting dialog.
            console.log("Reconnecting...");

            connected.current = false;
            reconnect();

            reconnectDialogTimeout = setTimeout(() => {
                console.log("Showing reconnect dialog.");
                setConnectedState(false);
            }, 5000);
        }, 5000);

        // Clean up in case this will ever be unmounted
        return () => {
            pingTimeout && clearTimeout(pingTimeout);
        };
    }, [dispatch, reconnect]);

    const processEvent = useCallback((event: StreamingEvent): boolean => {
        if (StreamingEventTypeAssertions.isExecutionsUpdatedStreamingEvent(event)) {
            if (store.getState().executions.loading) {
                return false;
            }
            console.log("Executions have been updated", event);
            dispatch(updateExecutionsManually({
                update: event.data
            }));
            return true;
        }
        if (StreamingEventTypeAssertions.isExecutionsDeletedStreamingEvent(event)) {
            if (store.getState().executions.loading) {
                return false;
            }
            console.log("Executions have been deleted", event);
            dispatch(updateExecutionsManually({
                delete: event.data
            }));
            return true;
        }
        if (StreamingEventTypeAssertions.isExecutionErrorsUpdatedStreamingEvent(event)) {
            if (store.getState().executionErrors.loading) {
                return false;
            }
            console.log("Execution errors have been updated", event);
            dispatch(updateExecutionErrorsManually({
                update: event.data
            }));
            return true;
        }
        if (StreamingEventTypeAssertions.isExecutionErrorsDeletedStreamingEvent(event)) {
            if (store.getState().executionErrors.loading) {
                return false;
            }
            console.log("Execution errors have been deleted", event);
            dispatch(updateExecutionErrorsManually({
                delete: event.data
            }));
            return true;
        }

        if (StreamingEventTypeAssertions.isRolesUpdatedStreamingEvent(event)) {
            if (store.getState().roles.loading) {
                return false;
            }
            console.log("Roles have been updated", event);
            dispatch(updateRolesManually({
                update: event.data
            }));
            return true;
        }

        if (StreamingEventTypeAssertions.isScenariosUpdatedStreamingEvent(event)) {
            if (store.getState().scenarios.loading) {
                return false;
            }
            console.log("Scenarios have been updated", event);
            dispatch(updateScenariosManually({
                update: event.data
            }));
            return true;
        }

        if (StreamingEventTypeAssertions.isWorkersDeletedStreamingEvent(event)) {
            if (store.getState().workers.loading) {
                return false;
            }
            console.log("Workers have been deleted", event);
            dispatch(updateWorkersManually({
                delete: event.data
            }));
            return true;
        }

        if (StreamingEventTypeAssertions.isWorkersUpdatedStreamingEvent(event)) {
            if (store.getState().workers.loading) {
                return false;
            }
            console.log("Workers have been updated", event);
            dispatch(updateWorkersManually({
                update: event.data
            }));
            return true;
        }

        // eslint-disable-next-line no-console
        console.error(`Unknown event received: ${JSON.stringify(event, undefined, 2)}`);
        assertNever(event);
        return true;
    }, [dispatch]);

    const processEventQueue = useCallback(() => {
        if (processing.current) {
            return;
        }
        processing.current = true;

        for (let i = 0; i < unprocessedEvents.current.length; i += 1) {
            const event = unprocessedEvents.current.shift();
            if (event) {
                const processed = processEvent(event);
                if (!processed) {
                    unprocessedEvents.current.push(event);
                }
            }
        }

        processing.current = false;
    }, [processEvent]);

    useEffect(() => {
        const interval = setInterval(processEventQueue, 100);
        return () => clearInterval(interval);
    }, [processEventQueue]);

    useEffect(() => {
        const group = LoginService.getParsedToken()?.groups[0];
        if (!group) {
            return undefined;
        }

        lastConnectionTime.current = new Date().getTime();
        const request: ConnectRequest = {
            applicationId: "qa-load-test",
            tenantId: group,
            timeSinceFirstConnect: undefined,
            deviceInfo: {
                architecture: "",
                cores: -1,
                memory: -1,
                model: "",
                osName: "",
                osVersion: "",
                screenDpr: -1,
                type: "",
                screenHeight: -1,
                screenWidth: -1,
                vendor: ""
            },
            connectionInfo: {
                connectionType: "",
                rtt: -1,
                downLink: -1
            },
            browserInfo: {
                browserName: "",
                browserVersionFull: "",
                browserVersionMajor: "",
                engineName: "",
                engineVersion: "",
                userAgent: "",
                withClientHints: false
            },
            sessionId: v4().substring(0, 8),
            reason: ConnectRequest_ConnectReason.INITIAL
        };

        abortController = new AbortController();
        const stream = connectClient.current.connect(request, {
            abort: abortController.signal,
            meta: getMetadata().metadata
        });

        let dataReloaded = false;
        stream.responses.onNext(data => {
            if (!data) {
                return;
            }
            // Reload all currently loaded states to ensure all data is up-to-date after an
            // intentional reconnect
            if (!dataReloaded && !firstTime) {
                dataReloaded = true;
                reloadAllState(dispatch);
            } else if (firstTime) {
                dataReloaded = true;
            }
            const eventData: IncomingEvent = {
                event: JSON.parse(
                    Base64.decode(data.payload)
                ),
                id: data.eventId
            };

            if (eventData.event.eventType === "PING") {
                onPing();
            } else if (eventData.event.eventType === "INIT") {
                connectionId.current = eventData.event.data.data.connectionId;
                console.log(`Initialized connection with id #${connectionId.current}.`);
                // Reset unacknowledged events because connection ID has changed.
                // Acknowledgements would fail anyway.
                unacknowledgedEvents.current = [eventData.id];
            } else {
                console.log(`Received event #${eventData.id}: ${eventData.event.data.eventName}`);

                // Always process a non-confirmed event. This is an idempotent operation.
                unprocessedEvents.current.push(eventData.event.data);

                // Add event to confirmation queue if it is not already in there
                if (unacknowledgedEvents.current.indexOf(eventData.id) === -1) {
                    unacknowledgedEvents.current.push(eventData.id);
                }
            }
        });

        stream.responses.onError(error => {
            console.log(`[gRPC] ERROR ${error.name}: ${error.message}`);
            reconnect();
        });
        stream.responses.onComplete(() => {
            console.log("Connection ended, reconnecting...");
            reconnect();
        });
    }, [dispatch, reconnect, onPing, key]);

    // Reconnect Stream every 15 minutes (= 900 sec)
    useEffect(() => {
        const interval = setInterval(() => {
            console.log("Reconnecting stream because of interval...");
            abortController?.abort("Reconnecting stream because of interval...");
        }, 900_000);
        return () => {
            clearInterval(interval);
        };
    }, [reconnect]);

    // Acknowledge events every 3 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            if (connected.current && connectionId.current && unacknowledgedEvents.current.length > 0) {
                const unacknowledged = unacknowledgedEvents.current.splice(0, unacknowledgedEvents.current.length);
                console.log(`Acknowledging ${unacknowledged.length} events: ${unacknowledged.join(", ")}`);
                try {
                    const request: ConfirmRequest = {
                        connectionId: connectionId.current,
                        events: unacknowledged.map(id => {
                            return {
                                eventId: id
                            } as ConfirmRequest_Event;
                        })
                    };
                    confirmClient.current.confirm(request, { meta: getMetadata().metadata }).then(value => {
                        if (value.status.code !== "OK") {
                            // eslint-disable-next-line no-console
                            console.error(`Could not confirm events ${unacknowledged.join(", ")}`, value.status.code, value.status.detail);
                            unacknowledgedEvents.current.push(...unacknowledged);
                        } else {
                            console.log(`${unacknowledged.length} events confirmed`);
                        }
                    });
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(`Failed to confirm events ${unacknowledged.join(", ")}`, e);
                    unacknowledgedEvents.current.push(...unacknowledged);
                }
            }
        }, 3000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    return (
        <>
            <Dialog
                className={classes.dialog}
                open={!connectedState}
                PaperProps={{
                    className: classes.paper,
                    elevation: 16
                }}>

                <DialogTitle>
                    {connectedOnce ? "Verbindung wiederherstellen..." : "Verbindung herstellen..."}
                </DialogTitle>

                <DialogContent>

                    <div className={classes.loadingWrapper}>
                        <CircularProgress />
                    </div>

                    <Typography>
                        Bitte warten Sie, während die Verbindung {connectedOnce ? "wieder" : ""}hergestellt wird.
                    </Typography>

                </DialogContent>

                <DialogActions />

            </Dialog>

        </>
    );
};

export default EventStreamer;
